import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermissionTemplates } from '../../services/permissionService';

export type SidebarItem = {
  href: string;
  icon: React.ReactNode;
  text: string;
  badge?: string;
  tabs?: {
    href: string;
    text: string;
  }[];
};

export type SidebarGroup = {
  title: string;
  items: SidebarItem[];
};

interface AdminSidebarProps {
  userData?: any;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ userData, isOpen, setIsOpen }) => {
  const location = useLocation();

  // load permission template and filter items by access
  const [template] = usePermissionTemplates(userData?.mainRole);
  const sectionsAccess = template?.sections || {};
  
  /**
   * Map each sidebar route to its permission key.
   * If a route maps to `null`, it is considered public (visible to all admins).
   */
  const hrefToSectionKey: Record<string, string | null> = {
    '/home': null,
    '/admin/access-control': null,
    '/admin/operators': null,
    '/admin/users': 'users',
    '/admin/ongoing-requests': 'ongoing_requests',
    '/admin/completed-requests': 'completed_requests',
    '/admin/others-requests': 'other_requests',
    '/admin/rules': 'rules',
    '/admin/instructions': 'instructions',
    '/admin/commands': 'instructions',
    '/admin/image-report-requests': 'image_reports',
    '/admin/messages': 'message',
    // '/admin/tickets': 'tickets', // Temporarily disabled - under maintenance
    '/admin/sms': 'sms',
    '/admin/calendar': 'calendar',
    '/admin/insurance': 'insurance',
    '/admin/test-results': 'test_result',
    '/admin/new-request': 'remaining_payment',
    '/admin/reports': 'reports',
    '/admin/region-settings': 'areas',
    '/admin/links': 'links',
    '/admin/guides': 'guides',
    '/admin/faqs': 'faqs',
    '/admin/test-packages': 'packages',
    '/admin/bank-payment-requests': 'remaining_payment',
    '/admin/general-settings': 'general_settings',
  };

  // Grouped menu items for better organization
  const sidebarGroups: SidebarGroup[] = [
    {
      title: "داشبورد",
      items: [
        {
          href: "/home",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 576 512" fill="currentColor">
              <path d="M575.8 255.5c0 18-15 32.1-32 32.1h-32l.7 160.2c0 2.7-.2 5.4-.5 8.1V472c0 22.1-17.9 40-40 40H456c-1.1 0-2.2 0-3.3-.1c-1.4 .1-2.8 .1-4.2 .1H416 392c-22.1 0-40-17.9-40-40V448 384c0-17.7-14.3-32-32-32H256c-17.7 0-32 14.3-32 32v64 24c0 22.1-17.9 40-40 40H160 128.1c-1.5 0-3-.1-4.5-.2c-1.2 .1-2.4 .2-3.6 .2H104c-22.1 0-40-17.9-40-40V360c0-.9 0-1.9 .1-2.8V287.6H32c-18 0-32-14-32-32.1c0-9 3-17 10-24L266.4 8c7-7 15-8 22-8s15 2 21 7L564.8 231.5c8 7 12 15 11 24z" />
            </svg>
          ),
          text: "داشبورد اصلی"
        }
      ]
    },
    {
      title: "مدیریت سیستم",
      items: [
        ...(userData?.mainRole === "SuperAdmin" ? [{
          href: "/admin/access-control",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 448 512" fill="currentColor">
              <path d="M144 144v48H304V144c0-44.2-35.8-80-80-80s-80 35.8-80 80zM80 192V144C80 64.5 144.5 0 224 0s144 64.5 144 144v48h16c35.3 0 64 28.7 64 64V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V256c0-35.3 28.7-64 64-64H80z" />
            </svg>
          ),
          text: "تعیین دسترسی"
        }] : []),
        ...(userData?.mainRole === "SuperAdmin" ? [{
          href: "/admin/operators",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 640 512" fill="currentColor">
              <path d="M96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM0 482.3C0 383.8 79.8 304 178.3 304h91.4C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7H29.7C13.3 512 0 498.7 0 482.3zM609.3 512H471.4c5.4-9.4 8.6-20.3 8.6-32v-8c0-60.7-27.1-115.2-69.8-151.8c2.4-.1 4.7-.2 7.1-.2h61.4C567.8 320 640 392.2 640 481.3c0 17-13.8 30.7-30.7 30.7zM432 256c-31 0-59-12.6-79.3-32.9C372.4 196.5 384 163.6 384 128c0-26.8-6.6-52.1-18.3-74.3C384.3 40.1 407.2 32 432 32c61.9 0 112 50.1 112 112s-50.1 112-112 112z" />
            </svg>
          ),
          text: "اپراتورها"
        }] : []),
        {
          href: "/admin/users",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 640 512" fill="currentColor">
              <path d="M144 0a80 80 0 1 1 0 160A80 80 0 1 1 144 0zM512 0a80 80 0 1 1 0 160A80 80 0 1 1 512 0zM0 298.7C0 239.8 47.8 192 106.7 192h74.6c11.3 0 22.2 2.8 31.9 7.9c43.1-4.2 87.5-14 125.8-14c35.8 0 72.1 4.9 108.2 14.1c9.7-5.1 20.7-8 32.2-8h74.6c58.9 0 106.7 47.8 106.7 106.7c0 52.3-37.9 95.8-88.2 104.4c.5 3.6 .7 7.2 .7 10.9v42.7c0 29.4-23.9 53.3-53.3 53.3H245.3c-29.4 0-53.3-23.9-53.3-53.3V416c0-3.7 .4-7.3 1-10.9C142.9 394.5 104.9 351 104.9 298.7h-.2zM53.3 298.7a53.3 53.3 0 1 0 106.7 0 53.3 53.3 0 1 0 -106.7 0zM458.7 352a53.3 53.3 0 1 0 0-106.7 53.3 53.3 0 1 0 0 106.7z" />
            </svg>
          ),
          text: "کاربران"
        }
      ]
    },
    {
      title: "درخواست‌ها",
      items: [
       
        {
          href: "/admin/ongoing-requests",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M256 0a256 256 0 1 1 0 512A256 256 0 1 1 256 0zM232 120V256c0 8 4 15.5 10.7 20l96 64c11 7.4 25.9 4.4 33.3-6.7s4.4-25.9-6.7-33.3L280 243.2V120c0-13.3-10.7-24-24-24s-24 10.7-24 24z" />
            </svg>
          ),
          text: "در حال اجرا"
        },
        {
          href: "/admin/completed-requests",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zM369 209L241 337c-9.4 9.4-24.6 9.4-33.9 0l-64-64c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0l47 47L335 175c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9z" />
            </svg>
          ),
          text: "نهایی و پرداخت شده"
        },
        {
          href: "/admin/others-requests",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M256 32c14.2 0 27.3 7.5 34.5 19.8l216 368c7.3 12.4 7.3 27.7 .2 40.1S486.3 480 472 480H40c-14.3 0-27.6-7.7-34.7-20.1s-7-27.8 .2-40.1l216-368C228.7 39.5 241.8 32 256 32zm0 128c-13.3 0-24 10.7-24 24V296c0 13.3 10.7 24 24 24s24-10.7 24-24V184c0-13.3-10.7-24-24-24zm32 224a32 32 0 1 0 -64 0 32 32 0 1 0 64 0z" />
            </svg>
          ),
          text: "سایر درخواست ها"
        },
        {
          href: "/admin/image-report-requests",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 576 512" fill="currentColor">
              <path d="M64 64C28.7 64 0 92.7 0 128V384c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64H64zm64 320H64V320c35.3 0 64 28.7 64 64zM64 192V128h64c0 35.3-28.7 64-64 64zM448 384c0-35.3 28.7-64 64-64v64H448zm64-192c-35.3 0-64-28.7-64-64h64v64zM288 160a96 96 0 1 1 0 192 96 96 0 1 1 0-192z" />
            </svg>
          ),
          text: "درخواست‌های تصویر جواب"
        },
        {
          href: "/admin/bank-payment-requests",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 576 512" fill="currentColor">
              <path d="M64 64C28.7 64 0 92.7 0 128v256c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V128c0-35.3-28.7-64-64-64H64zm80 152c0-22.1 17.9-40 40-40s40 17.9 40 40s-17.9 40-40 40s-40-17.9-40-40zM96 352a96 96 0 1 1 192 0A96 96 0 1 1 96 352zM464 208a32 32 0 1 1 0 64 32 32 0 1 1 0-64zm-16-80a16 16 0 1 1 32 0 16 16 0 1 1 -32 0zm16 144a16 16 0 1 1 0 32 16 16 0 1 1 0-32z" />
            </svg>
          ),
          text: "درخواست‌های پرداخت باقیمانده"
        }
      ]
    },
    {
      title: "تنظیمات و پیکربندی",
      items: [
        {
          href: "/admin/general-settings",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M495.9 166.6c3.2 8.7 .5 18.4-6.4 24.6l-43.3 39.4c1.1 8.3 1.7 16.8 1.7 25.4s-.6 17.1-1.7 25.4l43.3 39.4c6.9 6.2 9.6 15.9 6.4 24.6c-4.4 11.9-9.7 23.3-15.8 34.3l-4.7 8.1c-6.6 11-14 21.4-22.1 31.2c-5.9 7.2-15.7 9.6-24.5 6.8l-55.7-17.7c-13.4 10.3-28.2 18.9-44 25.4l-12.5 57.1c-2 9.1-9 16.3-18.2 17.8c-13.8 2.3-28 3.5-42.5 3.5s-28.7-1.2-42.5-3.5c-9.2-1.5-16.2-8.7-18.2-17.8l-12.5-57.1c-15.8-6.5-30.6-15.1-44-25.4L83.1 425.9c-8.8 2.8-18.6 .3-24.5-6.8c-8.1-9.8-15.5-20.2-22.1-31.2l-4.7-8.1c-6.1-11-11.4-22.4-15.8-34.3c-3.2-8.7-.5-18.4 6.4-24.6l43.3-39.4C64.6 273.1 64 264.6 64 256s.6-17.1 1.7-25.4L22.4 191.2c-6.9-6.2-9.6-15.9-6.4-24.6c4.4-11.9 9.7-23.3 15.8-34.3l4.7-8.1c6.6-11 14-21.4 22.1-31.2c5.9-7.2 15.7-9.6 24.5-6.8l55.7 17.7c13.4-10.3 28.2-18.9 44-25.4l12.5-57.1c2-9.1 9-16.3 18.2-17.8C227.3 1.2 241.5 0 256 0s28.7 1.2 42.5 3.5c9.2 1.5 16.2 8.7 18.2 17.8l12.5 57.1c15.8 6.5 30.6 15.1 44 25.4l55.7-17.7c8.8-2.8 18.6-.3 24.5 6.8c8.1 9.8 15.5 20.2 22.1 31.2l4.7 8.1c6.1 11 11.4 22.4 15.8 34.3zM256 336a80 80 0 1 0 0-160 80 80 0 1 0 0 160z" />
            </svg>
          ),
          text: "تنظیمات عمومی"
        },
        {
          href: "/admin/region-settings",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 576 512" fill="currentColor">
              <path d="M408 120c0 54.6-73.1 151.9-105.2 192c-7.7 9.6-22 9.6-29.6 0C241.1 271.9 168 174.6 168 120C168 53.7 221.7 0 288 0s120 53.7 120 120zm8 80.4c3.5-6.9 6.7-13.8 9.6-20.6c.5-1.2 1-2.5 1.5-3.7l116-46.4C558.9 123.4 576 135 576 152V422.8c0 9.8-6 18.6-15.1 22.3L416 503V200.4zM137.6 138.3c2.4 14.1 7.2 28.3 12.8 41.5c2.9 6.8 6.1 13.7 9.6 20.6V451.8L32.9 502.7C17.1 509 0 497.4 0 480.4V209.6c0-9.8 6-18.6 15.1-22.3l122.6-49zM327.8 332c13.9-17.4 35.7-45.7 56.2-77V504.3L192 449.4V255c20.5 31.3 42.3 59.6 56.2 77c20.5 25.6 59.1 25.6 79.6 0zM288 152a40 40 0 1 0 0-80 40 40 0 1 0 0 80z" />
            </svg>
          ),
          text: "مناطق"
        },
        {
          href: "/admin/test-packages",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M190.5 68.8L225.3 128H224 152c-22.1 0-40-17.9-40-40s17.9-40 40-40h2.2c14.9 0 28.8 7.9 36.3 20.8zM64 88c0 14.4 3.5 28 9.6 40H32c-17.7 0-32 14.3-32 32v64c0 17.7 14.3 32 32 32H480c17.7 0 32-14.3 32-32V160c0-17.7-14.3-32-32-32H438.4c6.1-12 9.6-25.6 9.6-40c0-48.6-39.4-88-88-88h-2.2c-31.9 0-61.5 16.9-77.7 44.4L256 85.5l-24.1-41C215.7 16.9 186.1 0 154.2 0H152C103.4 0 64 39.4 64 88zm336 0c0 22.1-17.9 40-40 40H288h-1.3l34.8-59.2C329.1 55.9 342.9 48 357.8 48H360c22.1 0 40 17.9 40 40zM32 288V464c0 26.5 21.5 48 48 48H224V352c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16V512H432c26.5 0 48-21.5 48-48V288H32z" />
            </svg>
          ),
          text: "پکیج آزمایشات"
        },
        {
          href: "/admin/calendar",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 448 512" fill="currentColor">
              <path d="M128 0c17.7 0 32 14.3 32 32V64H288V32c0-17.7 14.3-32 32-32s32 14.3 32 32V64h48c26.5 0 48 21.5 48 48v48H0V112C0 85.5 21.5 64 48 64H96V32c0-17.7 14.3-32 32-32zM0 192H448V464c0 26.5-21.5 48-48 48H48c-26.5 0-48-21.5-48-48V192zm64 80v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H80c-8.8 0-16 7.2-16 16zm128 0v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H208c-8.8 0-16 7.2-16 16zm144-16c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h32c8.8 0 16-7.2 16-16V272c0-8.8-7.2-16-16-16H336z" />
            </svg>
          ),
          text: "تقویم"
        },
        {
          href: "/admin/insurance",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M256 0c4.6 0 9.2 1 13.4 2.9L457.7 82.8c22 9.3 38.4 31 38.3 57.2c-.5 99.2-41.3 280.7-213.6 363.2c-16.7 8-36.1 8-52.8 0C57.3 420.7 16.5 239.2 16 140c-.1-26.2 16.3-47.9 38.3-57.2L242.7 2.9C246.8 1 251.4 0 256 0zm0 66.8V444.8C394 378 431.1 230.1 431.6 140L256 66.8z" />
            </svg>
          ),
          text: "بیمه"
        }
      ]
    },
    {
      title: "ارتباطات",
      items: [
        {
          href: "/admin/messages",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48H48zM0 176V384c0 35.3 28.7 64 64 64H448c35.3 0 64-28.7 64-64V176L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z" />
            </svg>
          ),
          text: "پیام‌ها"
        },
        // Temporarily disabled - under maintenance
        // {
        //   href: "/admin/tickets",
        //   icon: (
        //     <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 576 512" fill="currentColor">
        //       <path d="M64 32C28.7 32 0 60.7 0 96v32H576V96c0-35.3-28.7-64-64-64H64zM576 224H0V416c0 35.3 28.7 64 64 64H512c35.3 0 64-28.7 64-64V224zM112 352h64c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16s7.2-16 16-16zm112 16c0-8.8 7.2-16 16-16H368c8.8 0 16 7.2 16 16s-7.2 16-16 16H240c-8.8 0-16-7.2-16-16z" />
        //     </svg>
        //   ),
        //   text: "مدیریت تیکت‌ها"
        // },
      
      ]
    },
    {
      title: "محتوا و گزارشات",
      items: [
      
        {
          href: "/admin/commands",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M64 480H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H288c-10.1 0-19.6-4.7-25.6-12.8L243.2 57.6C231.1 41.5 212.1 32 192 32H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64z" />
            </svg>
          ),
          text: "دستورالعمل ها"
        },
      

        {
          href: "/admin/reports",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
              <path d="M0 64C0 28.7 28.7 0 64 0H224V128c0 17.7 14.3 32 32 32H384V448c0 35.3-28.7 64-64 64H64c-35.3 0-64-28.7-64-64V64zm384 64H256V0L384 128z" />
            </svg>
          ),
          text: "گزارشات"
        },
        {
          href: "/admin/links",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 640 512" fill="currentColor">
              <path d="M579.8 267.7c56.5-56.5 56.5-148 0-204.5c-50-50-128.8-56.5-186.3-15.4l-1.6 1.1c-14.4 10.3-17.7 30.3-7.4 44.6s30.3 17.7 44.6 7.4l1.6-1.1c32.1-22.9 76-19.3 103.8 8.6c31.5 31.5 31.5 82.5 0 114L422.3 334.8c-31.5 31.5-82.5 31.5-114 0c-27.9-27.9-31.5-71.8-8.6-103.8l1.1-1.6c10.3-14.4 6.9-34.4-7.4-44.6s-34.4-6.9-44.6 7.4l-1.1 1.6C206.5 251.2 213 330 263 380c56.5 56.5 148 56.5 204.5 0L579.8 267.7zM60.2 244.3c-56.5 56.5-56.5 148 0 204.5c50 50 128.8 56.5 186.3 15.4l1.6-1.1c14.4-10.3 17.7-30.3 7.4-44.6s-30.3-17.7-44.6-7.4l-1.6 1.1c-32.1 22.9-76 19.3-103.8-8.6C74 372 74 321 105.5 289.5L217.7 177.2c31.5-31.5 82.5-31.5 114 0c27.9 27.9 31.5 71.8 8.6 103.9l-1.1 1.6c-10.3 14.4-6.9 34.4 7.4 44.6s34.4 6.9 44.6-7.4l1.1-1.6C433.5 260.8 427 182 377 132c-56.5-56.5-148-56.5-204.5 0L60.2 244.3z" />
            </svg>
          ),
          text: "لینک‌ها"
        },
        {
          href: "/admin/guides",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M64 480H448c35.3 0 64-28.7 64-64V160c0-35.3-28.7-64-64-64H288c-10.1 0-19.6-4.7-25.6-12.8L243.2 57.6C231.1 41.5 212.1 32 192 32H64C28.7 32 0 60.7 0 96V416c0 35.3 28.7 64 64 64zM176 256c0-8.8 7.2-16 16-16H320c8.8 0 16 7.2 16 16s-7.2 16-16 16H192c-8.8 0-16-7.2-16-16zm0 64c0-8.8 7.2-16 16-16H320c8.8 0 16 7.2 16 16s-7.2 16-16 16H192c-8.8 0-16-7.2-16-16zM96 192c0-8.8 7.2-16 16-16h32c8.8 0 16 7.2 16 16s-7.2 16-16 16H112c-8.8 0-16-7.2-16-16z" />
            </svg>
          ),
          text: "راهنما"
        },
        {
          href: "/admin/faqs",
          icon: (
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 512 512" fill="currentColor">
              <path d="M256 0C114.6 0 0 114.6 0 256s114.6 256 256 256s256-114.6 256-256S397.4 0 256 0zM256 400c-18 0-32-14-32-32s14-32 32-32s32 14 32 32S274 400 256 400zM325.1 258L280 286V288c0 6.6-5.4 12-12 12s-12-5.4-12-12V272c0-6.6 5.4-12 12-12l61.1-40.7c5-3.3 11.4-3.9 16.9-1.5s9.4 8.3 9.4 14.2v64c0 5.9-3.2 11.3-8.4 14.2s-11.9 1.8-16.9-1.5L325.1 258z" />
            </svg>
          ),
          text: "سوالات متداول"
        }
      ]
    }
  ];

  // Filter groups and items based on permissions
  const getVisibleGroups = () => {
    return sidebarGroups.map(group => ({
      ...group,
      items: group.items.filter(item => {
        const key = hrefToSectionKey[item.href];
        if (key === null || key === undefined) return true;
        const hasAccess = sectionsAccess[key]?.access;

        return hasAccess;
      })
    })).filter(group => group.items.length > 0);
  };

  const visibleGroups = getVisibleGroups();

  const isActiveLink = (href: string) => {
    return location.pathname === href;
  };

  return (
    <aside className={`mt-10 fixed top-16 bottom-0 right-0 w-64 bg-white border-l border-gray-200 transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'} md:translate-x-0 z-20 overflow-y-auto`}>
      <div className="p-4 space-y-6">
        {visibleGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="space-y-2">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2">
              {group.title}
            </h3>
            <div className="space-y-1">
              {group.items.map((item, itemIndex) => (
                <React.Fragment key={`${groupIndex}-${itemIndex}`}>
                  <Link
                    to={item.href}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                      isActiveLink(item.href)
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className={`transition-colors duration-200 ${
                      isActiveLink(item.href) 
                        ? 'text-blue-600' 
                        : 'text-gray-500 group-hover:text-gray-700'
                    }`}>
                      {item.icon}
                    </div>
                    <span className="flex-1">{item.text}</span>
                    {item.badge && (
                      <span className="bg-red-100 text-red-600 text-xs font-medium px-2 py-0.5 rounded-full">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                  {item.tabs && item.tabs.length > 0 && (
                    <div className="mr-6 space-y-1">
                      {item.tabs.map((tab, tabIndex) => (
                        <Link
                          key={`${groupIndex}-${itemIndex}-${tabIndex}`}
                          to={tab.href}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-colors duration-200 ${
                            isActiveLink(tab.href)
                              ? 'bg-blue-50 text-blue-600'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-800'
                          }`}
                        >
                          <div className="w-1.5 h-1.5 rounded-full bg-current opacity-60"></div>
                          <span>{tab.text}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default AdminSidebar;